export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message?: string,
  ) {
    super(message ?? `HTTP ${status}`);
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

interface RequestOpts extends Omit<RequestInit, 'body'> {
  json?: unknown;
}

async function request<T>(url: string, opts: RequestOpts = {}): Promise<T> {
  const headers = new Headers(opts.headers);
  const method = (opts.method ?? 'GET').toUpperCase();

  if (opts.json !== undefined) {
    headers.set('content-type', 'application/json');
  }

  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrf = readCookie('csrf');
    if (csrf) headers.set('x-csrf-token', csrf);
  }

  const res = await fetch(url, {
    ...opts,
    method,
    headers,
    body: opts.json !== undefined ? JSON.stringify(opts.json) : (opts as RequestInit).body,
    credentials: 'same-origin',
  });

  if (res.status === 204) return null as T;

  let body: unknown;
  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    body = await res.json();
  } else {
    body = await res.text();
  }

  if (!res.ok) throw new ApiError(res.status, body);
  return body as T;
}

export const api = {
  get:    <T>(url: string, opts: RequestOpts = {}) => request<T>(url, { ...opts, method: 'GET' }),
  post:   <T>(url: string, json?: unknown, opts: RequestOpts = {}) => request<T>(url, { ...opts, method: 'POST', json }),
  patch:  <T>(url: string, json?: unknown, opts: RequestOpts = {}) => request<T>(url, { ...opts, method: 'PATCH', json }),
  del:    <T>(url: string, opts: RequestOpts = {}) => request<T>(url, { ...opts, method: 'DELETE' }),
};
